import React, { useState, useMemo } from 'react';
import { PlusIcon, SearchIcon, PencilIcon, BarChart3Icon, MailIcon, MessageSquareIcon, PhoneIcon, BellIcon, GlobeIcon, Share2Icon, UsersIcon, TargetIcon, MapPinIcon, FilterIcon, InboxIcon, MonitorIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { getCategoryLabel } from '../data/communications';
import type { ChannelKey, Notice, NoticeStatus } from '../types/notice';
const CHANNEL_ICONS: Record<ChannelKey, typeof MailIcon> = {
  email: MailIcon,
  sms: MessageSquareIcon,
  voice: PhoneIcon,
  push: BellIcon,
  website: GlobeIcon,
  social: Share2Icon,
  screens: MonitorIcon
};
const AUDIENCE_ICONS = {
  org: UsersIcon,
  sector: TargetIcon,
  location: MapPinIcon,
  filter: FilterIcon
};
interface NoticeListProps {
  notices: Notice[];
  onCreate: () => void;
  onEdit: (notice: Notice) => void;
  onViewStats: (notice: Notice) => void;
}
export function NoticeList({
  notices,
  onCreate,
  onEdit,
  onViewStats
}: NoticeListProps) {
  const {
    t,
    lang
  } = useLanguage();
  const [status, setStatus] = useState<NoticeStatus | 'all'>('all');
  const [query, setQuery] = useState('');
  const locale = lang === 'fr' ? 'fr-CA' : 'en-CA';
  const statusMeta: Record<NoticeStatus, {
    label: string;
    className: string;
    dot: string;
  }> = {
    sent: {
      label: t('Envoyé', 'Sent'),
      className: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      dot: 'bg-emerald-500'
    },
    scheduled: {
      label: t('Programmé', 'Scheduled'),
      className: 'bg-amber-50 text-amber-700 border-amber-200',
      dot: 'bg-amber-500'
    },
    draft: {
      label: t('Brouillon', 'Draft'),
      className: 'bg-slate-100 text-slate-600 border-slate-200',
      dot: 'bg-slate-400'
    }
  };
  const audienceLabel = (notice: Notice) => {
    if (notice.audience.detail) return notice.audience.detail[lang];
    switch (notice.audience.kind) {
      case 'org':
        return t("Toute l'organisation", 'Entire organization');
      case 'sector':
        return t('Secteurs ciblés', 'Targeted districts');
      case 'location':
        return t('Autour d’un lieu', 'Around a location');
      default:
        return t('Audience filtrée', 'Filtered audience');
    }
  };
  const filters: {
    id: NoticeStatus | 'all';
    label: string;
  }[] = [{
    id: 'all',
    label: t('Tous', 'All')
  }, {
    id: 'sent',
    label: t('Envoyés', 'Sent')
  }, {
    id: 'scheduled',
    label: t('Programmés', 'Scheduled')
  }, {
    id: 'draft',
    label: t('Brouillons', 'Drafts')
  }];
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return notices.filter(n => status === 'all' ? true : n.status === status).filter(n => q ? n.title.toLowerCase().includes(q) || getCategoryLabel(n.category)[lang].toLowerCase().includes(q) : true).sort((a, b) => +new Date(b.date) - +new Date(a.date));
  }, [notices, status, query, lang]);
  const countFor = (id: NoticeStatus | 'all') => id === 'all' ? notices.length : notices.filter(n => n.status === id).length;
  return <div className="space-y-6 animate-in fade-in duration-300" data-id="element-4173" data-mp="2acdu3nbywrav">
      {/* Toolbar */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-4 justify-between" data-id="element-4174" data-mp="2f5yq59u03b7f">
        <div className="flex flex-wrap items-center gap-2" data-id="element-4175" data-mp="2k3qlkdtutfgd">
          {filters.map(f => {
          const active = status === f.id;
          return <button key={f.id} onClick={() => setStatus(f.id)} className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-colors border ${active ? 'bg-[#0F172A] text-white border-[#0F172A]' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`} data-id="element-4176" data-mp="1rf2vm4y5u1wh">
                {f.label}
                <span className={`ml-2 text-xs ${active ? 'text-slate-300' : 'text-slate-400'}`} data-id="element-4177" data-mp="3crdz58wpj5ti">
                  {countFor(f.id)}
                </span>
              </button>;
        })}
        </div>

        <div className="flex items-center gap-3" data-id="element-4178" data-mp="3kc6rj0i97to1">
          <div className="relative flex-1 lg:w-72" data-id="element-4179" data-mp="3ke1awadvndq0">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" data-id="element-4180" data-mp="3fdn5l03nuhvo" />
            <input type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder={t('Rechercher un avis…', 'Search notices…')} aria-label={t('Rechercher un avis', 'Search notices')} className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#4F46E5] focus:border-[#4F46E5]" data-id="element-4181" data-mp="3tc396i687s9l" />
          </div>
          <button onClick={onCreate} className="shrink-0 inline-flex items-center gap-2 px-4 py-2 bg-[#4F46E5] hover:bg-[#4338CA] text-white text-sm font-semibold rounded-lg transition-colors shadow-sm" data-id="element-4182" data-mp="bwwrrz1u70jf">
            <PlusIcon className="w-4 h-4" data-id="element-4183" data-mp="ziqp14ztt30x" />
            {t('Créer un avis', 'Create a notice')}
          </button>
        </div>
      </div>

      {/* List */}
      {visible.length === 0 ? <div className="bg-white rounded-xl border border-slate-200 py-16 px-6 text-center" data-id="element-4184" data-mp="qiap25iake0t">
          <div className="w-12 h-12 mx-auto rounded-full bg-slate-100 flex items-center justify-center mb-3" data-id="element-4185" data-mp="btehsao27519">
            <InboxIcon className="w-5 h-5 text-slate-400" data-id="element-4186" data-mp="glz9n6nen825" />
          </div>
          <p className="text-sm font-medium text-slate-600" data-id="element-4187" data-mp="2uausvlru5hh">
            {t('Aucun avis ne correspond à ces critères.', 'No notice matches these criteria.')}
          </p>
          <p className="text-xs text-slate-400 mt-1" data-id="element-4188" data-mp="3p0zdy500d1to">
            {t('Ajustez la recherche ou créez un nouvel avis.', 'Adjust your search or create a new notice.')}
          </p>
        </div> : <ul className="space-y-2" data-id="element-4189" data-mp="3aw8v87u19af8">
          {visible.map(notice => {
        const meta = statusMeta[notice.status];
        const AudienceIcon = AUDIENCE_ICONS[notice.audience.kind];
        return <li key={notice.id} onDoubleClick={() => onEdit(notice)} className="group bg-white rounded-xl border border-slate-200 px-5 py-4 hover:border-[#4F46E5]/40 hover:shadow-sm transition-all" data-id="element-4190" data-mp="315xetlt5x8rg">
                <div className="flex items-center gap-4" data-id="element-4191" data-mp="3ouyt3poofe4n">
                  {/* Status + category, stacked so neither crowds the title. */}
                  <div className="hidden sm:flex flex-col gap-1 w-32 shrink-0" data-id="element-4192" data-mp="3juatwgqanqzc">
                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border w-fit ${meta.className}`} data-id="element-4193" data-mp="3ewj01h639usi">
                      <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} data-id="element-4194" data-mp="23h45ek1crf0k" />
                      {meta.label}
                    </span>
                    <span className="px-2 py-0.5 rounded-full bg-slate-50 text-slate-600 text-xs font-medium border border-slate-200 w-fit truncate max-w-full" data-id="element-4195" data-mp="iy0ebn55wtf5">
                      {getCategoryLabel(notice.category)[lang]}
                    </span>
                  </div>

                  <div className="min-w-0 flex-1" data-id="element-4196" data-mp="xq28cqkjws79">
                    <h3 className="text-base font-semibold text-[#0F172A] truncate" data-id="element-4197" data-mp="12odjd4ml18r2">
                      {notice.title}
                    </h3>

                    <div className="flex items-center gap-3 flex-wrap mt-1 text-xs text-slate-500" data-id="element-4198" data-mp="16nmby65vz0l1">
                      <span data-id="element-4199" data-mp="1bpbpbsgkzqca">
                        {notice.status === 'scheduled' ? t('Envoi prévu le ', 'Sending on ') : notice.status === 'draft' ? t('Modifié le ', 'Edited on ') : t('Envoyé le ', 'Sent on ')}
                        <span className="font-medium text-slate-700" data-id="element-4200" data-mp="1o74k9kzrqyt6">
                          {new Date(notice.date).toLocaleDateString(locale, {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    })}
                        </span>
                      </span>
                      <span aria-hidden="true" className="text-slate-300" data-id="element-4201" data-mp="1symjo4m58rc1">
                        ·
                      </span>
                      <span className="inline-flex items-center gap-1.5" data-id="element-4202" data-mp="1096zopci3o3z">
                        <AudienceIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-4203" data-mp="10b9y9xrpypkb" />
                        <span className="font-medium text-slate-700" data-id="element-4204" data-mp="1f80zmckyebck">
                          {notice.audience.count.toLocaleString(locale)}
                        </span>
                        <span className="hidden md:inline" data-id="element-4205" data-mp="2qkto9eig6joe">
                          {audienceLabel(notice)}
                        </span>
                      </span>
                      <span aria-hidden="true" className="text-slate-300" data-id="element-4206" data-mp="2t614tv3q74os">
                        ·
                      </span>
                      <span className="inline-flex items-center gap-1.5" data-id="element-4207" data-mp="2y5e0948nxk2z">
                        {notice.channels.map(c => {
                    const Icon = CHANNEL_ICONS[c];
                    return <Icon key={c} className="w-3.5 h-3.5 text-slate-400" aria-hidden="true" data-id="element-4208" data-mp="2o7gecne9zzdj" />;
                  })}
                        <span className="sr-only" data-id="element-4209" data-mp="3350dhdohr0bs">
                          {notice.channels.length} {t('canaux', 'channels')}
                        </span>
                      </span>
                    </div>
                  </div>

                  {notice.status === 'sent' && notice.stats && <div className="hidden xl:flex items-center gap-5 shrink-0 text-right" data-id="element-4210" data-mp="3gz87br0d20d">
                      <div data-id="element-4211" data-mp="cs6me17tj5u0">
                        <div className="text-sm font-bold text-[#0F172A]" data-id="element-4212" data-mp="cszmwkg6oz3p">
                          {notice.stats.openRate} %
                        </div>
                        <div className="text-[11px] text-slate-400" data-id="element-4213" data-mp="2us7i61jmp3t">
                          {t('Ouverture', 'Open rate')}
                        </div>
                      </div>
                      <div data-id="element-4214" data-mp="3q7crp6ckzyga">
                        <div className="text-sm font-bold text-[#0F172A]" data-id="element-4215" data-mp="3q72x15gld349">
                          {notice.stats.clickRate} %
                        </div>
                        <div className="text-[11px] text-slate-400" data-id="element-4216" data-mp="3uzw4i13g2pk1">
                          {t('Clics', 'Clicks')}
                        </div>
                      </div>
                    </div>}

                  {/* Revealed on hover — kept reachable by keyboard at all times. */}
                  <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity" data-id="element-4217" data-mp="3ba2gzz7riwlm">
                    <button onClick={() => onEdit(notice)} aria-label={t(`Modifier « ${notice.title} »`, `Edit “${notice.title}”`)} title={t('Modifier', 'Edit')} className="p-2 rounded-lg text-slate-500 hover:text-[#4F46E5] hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-[#4F46E5] transition-colors" data-id="element-4218" data-mp="2smkxc933ou46">
                      <PencilIcon className="w-4 h-4" data-id="element-4219" data-mp="36krnt45pzrtt" />
                    </button>
                    <button onClick={() => onViewStats(notice)} aria-label={t(`Statistiques de « ${notice.title} »`, `Statistics for “${notice.title}”`)} title={t('Statistiques', 'Statistics')} className="p-2 rounded-lg text-slate-500 hover:text-[#4F46E5] hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-[#4F46E5] transition-colors" data-id="element-4220" data-mp="3kd7kzobxwvmt">
                      <BarChart3Icon className="w-4 h-4" data-id="element-4221" data-mp="2r0ra1cqygkps" />
                    </button>
                  </div>
                </div>
              </li>;
      })}
        </ul>}
    </div>;
}